import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { RadioGroup } from "@trendmicro/react-radio";
import FlexContainer from "./FlexContainer";
import { 
    Button,
    LottieLoadingIndicator,
    PortalActionBar,
    PortalCaption,
    PortalIconActionButton,
    PortalMetricCard,
    portalColors
} from "src/components";

import { RadioButton } from "@trendmicro/react-radio";
import CompanyPaymentMethods, { GenericPaymentMethod } from "src/scenes/Subscription/api/CompanyPaymentMethods";
import Toast from "./Toast";
import ImgAirwallexDesktopLogo from "src/img/airwallex@2x.png";
import Amex from "src/img/airwallex/Amex.svg";
import GenericCard from "src/img/airwallex/GenericCard.svg";
import Mastercard from "src/img/airwallex/Mastercard.svg";
import Visa from "src/img/airwallex/Visa.svg";

export const ButtonContainer = styled.div`
    display: flex;
    margin: ${p => p.margin || "64px 0"};
    justify-content: ${p => p.justifyContent || "center"};
    width: ${p => p.width};
    height: auto;
`;

export const NewRadioButton = styled(RadioButton)`
    margin: 0.4rem 0;
    font-weight: 500;
    color: ${portalColors.text};

  .radio---control-radio---2rwkY:not(.radio---disabled---eCY9b):hover {
    border: 1px solid ${portalColors.text};
  }

  .radio---control-radio---2rwkY:not(.radio---disabled---eCY9b):hover, >
  .radio---input-radio---2yM_b:checked {
    border: 1px solid ${portalColors.text};
    background-color: ${portalColors.text};
  }

  .radio---input-radio---2yM_b:checked + .radio---control-indicator---dcwXW::before {
    border: 1px solid ${portalColors.text};
    background-color: ${portalColors.text};
  }
`;

const CardListStack = styled.div`
    width: 100%;
    min-height: ${p => p.minHeight || "31rem"};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 1rem;
`;

const PaymentMethodsCard = styled(PortalMetricCard)`
    width: 100%;
    max-width: 24rem;
    min-height: auto;
    align-content: start;
`;

const CardDetailsContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${portalColors.text};
`;

const CardLogo = styled.img`
  height: 1.5rem;
  margin-left: .5rem;
`;

const StyledButton = styled(Button)`
    width: ${p => p.width || "8.75rem"};
    height: 2.625rem;
    border-radius: var(--calendar-radius-control, 8px) !important;
    line-height: 100%;
    font-weight: 700;
    font-size: 0.875rem;
    box-shadow: none;
`;

const ProviderText = styled.div`
    display: flex;
    align-items: center;
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.4;
`;

const ProviderLogo = styled.img`
    height: 0.85rem;
    margin-left: 1em;
`;

function CardList(props) {
    const [ isLoadingPaymentMethods, setIsLoadingPaymentMethods ] = useState(false);
    const [ isProcessingPayment, setIsProcessingPayment ] = useState(false);
    const [ paymentMethods, setPaymentMethods ] = useState(GenericPaymentMethod.default());
    const [ selectedMethod, setSelectedMethod ] = useState("");

    /** @type string|null */
    const loadingError = props.loadingError;

    const token = props.token;
    const onBackClicked = props.onBackClicked;
    const onPurchaseClicked = props.onPurchaseClicked;
    const onDeleteClicked = props.onDeleteClicked;
    const onCardsLoaded = props.onCardsLoaded;
    const supportedSources = props.supportedSources;
    const hideBackButton = props.hideBackButton;

    const findMethodSource = methodId => {
        const foundMethod = paymentMethods.find(method => method.id === methodId);
        return foundMethod ? foundMethod.source : "";
    };

    const thisPurchaseClicked = async (selectedMethod) => {
        setIsProcessingPayment(true);
        try {
            await onPurchaseClicked(selectedMethod, findMethodSource(selectedMethod));
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const loadPaymentMethods = async () => {
        setIsLoadingPaymentMethods(true);
        try {
            const methods = await CompanyPaymentMethods.get(token);
            const data = methods.intoDataArray()
                .filter(method => !supportedSources || supportedSources.includes(method.source));

            setPaymentMethods(data);

            if (onCardsLoaded) {
                onCardsLoaded(data);
            }

            const defaultMethod = data.find(method => method.isDefault)?.id || "";
            if (defaultMethod) {
                setSelectedMethod(defaultMethod);
            }
        } catch (error) {
            Toast.error(error.getApiErrorDetails().message);
        } finally {
            setIsLoadingPaymentMethods(false);
        }
    };

    const renderCardLogo = (brand) => {
        const logoMap = {
            "american express": Amex,
            "mastercard": Mastercard,
            "visa": Visa,
        };
        
        const logo = logoMap[brand] || GenericCard;
        
        return <CardLogo src={logo} />;
    };

    useEffect(() => {
        loadPaymentMethods();
    }, []);

    return (
        <CardListStack minHeight={props.minHeight}>

            {!isProcessingPayment && 
                <>
                    <PaymentMethodsCard>
                        <RadioGroup name="card" value={selectedMethod}>
                            <FlexContainer direction="column">
                                { isLoadingPaymentMethods && <PortalCaption margin="0">Getting payment method information...</PortalCaption> }
                                { !isLoadingPaymentMethods && paymentMethods.length === 0 && <PortalCaption margin="0">No existing payment methods. Add a new card.</PortalCaption> }
                                { loadingError && <PortalCaption margin="0" color={portalColors.danger}>{loadingError}</PortalCaption> }
                                { paymentMethods.map((method, index) => {
                                    return (
                                        <CardDetailsContainer key={index}>
                                            <NewRadioButton
                                                key={method.id}
                                                style={{ marginLeft: "10px" }}
                                                value={method.id}
                                                label={ "•••• •••• •••• " + method.cardLast4 }
                                                checked={ selectedMethod === method.id}
                                                onChange={() => setSelectedMethod(method.id)} 
                                            />
                                            { renderCardLogo(method.cardBrand) }
                                        </CardDetailsContainer>
                                    );
                                })}
                            </FlexContainer>
                        </RadioGroup>
                    </PaymentMethodsCard>
                </> 
            }

            <div>
                <PortalActionBar
                    justifyContent="flex-start"
                    padding="0"
                    border="transparent"
                    shadow="none"
                    radius="0"
                >
                    { onDeleteClicked && !isLoadingPaymentMethods &&
                        <StyledButton
                            width="6.25rem"
                            backgroundColor={portalColors.danger}
                            color="#FFFFFF"
                            border={`1px solid ${portalColors.danger}`}
                            onClick={ () => onDeleteClicked(selectedMethod, findMethodSource(selectedMethod)) }
                        >
                            DELETE
                        </StyledButton>
                    }
                
                    { isLoadingPaymentMethods || isProcessingPayment ?
                        <LottieLoadingIndicator />
                        :
                        <React.Fragment>
                            {!hideBackButton && (
                                <StyledButton
                                    width="8.75rem"
                                    backgroundColor={portalColors.surface}
                                    color={portalColors.text}
                                    border={`1px solid ${portalColors.border}`}
                                    onClick={() => onBackClicked()}
                                >
                                    Back
                                </StyledButton>
                            )}
                            <PortalIconActionButton
                                type="button"
                                width="11.125rem"
                                minWidth="11.125rem"
                                onClick={() => thisPurchaseClicked(selectedMethod)}
                            >
                                Pay
                            </PortalIconActionButton>
                        </React.Fragment>
                    }
                </PortalActionBar>
                <FlexContainer marginTop="2.89rem">
                    <ProviderText>
                        Hosted by Airwallex
                        <ProviderLogo src={ImgAirwallexDesktopLogo} alt="Airwallex Logo" />
                    </ProviderText>
                    <PortalCaption margin="0.625rem 0 0 0" maxWidth="24.5625rem" color={portalColors.mutedText}>
                        *Your payment method will be stored on your account for subscription, renewals and future payments.
                    </PortalCaption>
                </FlexContainer>
            </div>
            
        </CardListStack>
    );
}

export default CardList;
